import * as React from 'react';
import PropTypes from 'prop-types';
import { Global } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { grey } from '@mui/material/colors';
import Typography from '@mui/material/Typography';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import SendIcon from '@mui/icons-material/Send';

const drawerBleeding = 100;

const StyledBox = styled('div')(({ theme }) => ({
  backgroundColor: '#222',
  [theme.breakpoints.down('md')]: {
    backgroundColor: grey[800],
  },
}));

const Puller = styled('div')(({ theme }) => ({
  width: 30,
  height: 6,
  backgroundColor: grey[300],
  borderRadius: 3,
  position: 'absolute',
  top: 8,
  left: 'calc(50% - 15px)',
  [theme.breakpoints.down('md')]: {
    backgroundColor: grey[900],
  },
}));

function NotesDrawer(props) {
  const { window, open, setOpen, notes, setNotes, user } = props;
  const [localOpen, setLocalOpen] = React.useState(false);
  const [inputText, setInputText] = React.useState('');

  const toggleDrawer = (newOpen) => () => {
    if (setOpen) {
      setOpen(newOpen);
    } else {
      setLocalOpen(newOpen);
    }
  };

  const isOpen = open !== undefined ? open : localOpen;
  const container = window !== undefined ? () => window().document.body : undefined;

  const addNote = () => {
    if (inputText.trim() !== '') {
      const update = [...notes];
      update.push(user.data.name + " - " + inputText);
      setInputText('');
      setNotes(update);
    }
  };

  return (
    <>
      <CssBaseline />
      <Global
        styles={{
          '.MuiDrawer-root > .MuiPaper-root': {
            height: `calc(50% - ${drawerBleeding}px)`,
            overflow: 'visible',
            backgroundColor: '#222',
          },
        }}
      />
      <SwipeableDrawer
        container={container}
        anchor="bottom"
        open={isOpen}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        swipeAreaWidth={drawerBleeding}
        disableSwipeToOpen={false}
        keepMounted
        sx={{ zIndex: 3500 }}
      >
        <StyledBox
          sx={{
            position: 'absolute',
            top: -drawerBleeding,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            visibility: 'visible',
            right: 0,
            left: 0,
          }}
        >
          <Puller />
          <Typography sx={{ p: 2, pb: 6.8, color: 'text.secondary', fontSize: 21 }}>
            {notes ? `${notes.length} notes` : 'Notes'}
          </Typography>
        </StyledBox>
        <StyledBox sx={{ px: 2, pb: 2, pt: 4, height: '100%', overflow: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            minHeight: 120,
            maxHeight: 240,
            overflowY: 'auto',
            marginBottom: 16
          }}>
            {notes && notes.length > 0 && user ? notes.map((note, idx) => {
              const splitIdx = note.indexOf(' - ');
              const userName = splitIdx !== -1 ? note.slice(0, splitIdx) : '';
              const message = splitIdx !== -1 ? note.slice(splitIdx + 3) : note;
              const isMe = user.data.name === userName;
              return (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isMe ? 'flex-end' : 'flex-start',
                    width: '100%'
                  }}
                >
                  <span style={{
                    fontSize: 12,
                    color: '#bbb',
                    marginBottom: 2,
                    marginLeft: isMe ? 0 : 8,
                    marginRight: isMe ? 8 : 0,
                    alignSelf: isMe ? 'flex-end' : 'flex-start',
                  }}>{userName}</span>
                  <span style={{
                    background: isMe ? '#1976d2' : '#181818',
                    color: '#fff',
                    borderRadius: 16,
                    padding: '10px 16px',
                    maxWidth: '75%',
                    wordBreak: 'break-word',
                    boxShadow: isMe ? '0 2px 8px rgba(25,118,210,0.10)' : '0 2px 8px rgba(0,0,0,0.10)',
                    alignSelf: isMe ? 'flex-end' : 'flex-start',
                  }}>{message}</span>
                </div>
              );
            }) : null}
          </div>
          <Paper
            elevation={2}
            sx={{
              p: '8px 16px',
              display: 'flex',
              alignItems: 'center',
              mt: 3,
              borderRadius: 2,
              bgcolor: '#181818',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <InputBase
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Add a note..."
              sx={{ ml: 1, flex: 1, color: '#fff', '::placeholder': { color: '#bbb' }, width: '100%' }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  addNote();
                }
              }}
            />
            <IconButton 
              onClick={addNote}
              sx={{ 
                ml: 1,
                bgcolor: '#333',
                color: 'white',
                '&:hover': {
                  bgcolor: '#444',
                }
              }}
            >
              <SendIcon />
            </IconButton>
          </Paper>
        </StyledBox>
      </SwipeableDrawer>
    </>
  );
}

NotesDrawer.propTypes = {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window: PropTypes.func,
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  notes: PropTypes.array,
  setNotes: PropTypes.func,
  user: PropTypes.object,
};

export default NotesDrawer;